import React from 'react';
import { ButtonProps } from '@mui/material/Button';
import { useThemeApp } from 'components/Theme/theme';
import { Button, CircularProgress, Box } from '@mui/material';

interface ThemedButtonProps extends Omit<ButtonProps, 'onClick' | 'type'> {
    buttonType?: 'submit' | 'cancel';
    onClick?: (e: any) => void;
    children: React.ReactNode;
    isLoading?: boolean;
}

function ThemedButton({
    onClick,
    children,
    isLoading = false,
    buttonType = 'submit',
    ...rest
}: ThemedButtonProps) {
    const { colors } = useThemeApp()

    const commonSx = {
        padding: '14px',
        borderRadius: 2,
        fontWeight: 600,
        fontSize: '1rem',
        textTransform: 'none',
    };

    const getButtonSx = () => {
        switch (buttonType) {
            case 'cancel':
                return {
                    ...commonSx,
                    color: colors.text,
                    borderColor: colors.border,
                    '&:hover': {
                        borderColor: colors.primary,
                        background: colors.primary + '10',
                    }
                };
            case 'submit':
            default:
                return {
                    ...commonSx,
                    color: colors.white,
                    transition: 'all 0.3s ease',
                    boxShadow: `0 4px 20px ${colors.primary}40`,
                    background: `linear-gradient(45deg, ${colors.primary}, ${colors.primary}80)`,
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 25px ${colors.primary}60`,
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                    },
                    '&.Mui-disabled': {
                        background: colors.border,
                    }
                };
        }
    };

    return (
        <Button
            onClick={onClick}
            disabled={isLoading || rest.disabled}
            sx={getButtonSx()}
            {...rest}
        >
            {isLoading && (
                <Box sx={{ width: '100%', height: '100%', position: 'absolute', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={20} />
                </Box>
            )}
            <Box sx={{ visibility: isLoading ? 'hidden' : 'visible' }}>
                {children}
            </Box>
        </Button>
    );
}

export default ThemedButton;